import { LatLng } from './map'

/**
 * Where the map looks to frame a set of pins, worked out in Web Mercator
 * rather than by Google's `fitBounds`: `fitBounds` animates, and it overshoots
 * the zoom cap before it can be corrected, so switching days zoomed far in and
 * back out over tiles that had not loaded. A computed camera is applied in one
 * jump.
 */

export interface Camera {
	center: LatLng
	zoom: number
}

export interface ViewportSize {
	width: number
	height: number
}

export interface FramePadding {
	top: number
	bottom: number
	left: number
	right: number
}

const TILE_SIZE = 256
const MAX_MERCATOR_LAT = 85.05112878

const toWorldX = (lng: number): number => (lng + 180) / 360

const toWorldY = (lat: number): number => {
	const sin = Math.sin((Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, lat)) * Math.PI) / 180)
	return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)
}

const toLng = (x: number): number => (x - Math.floor(x)) * 360 - 180

const toLat = (y: number): number => (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI

/** The narrowest west-to-east arc holding every longitude, so pins either side of the antimeridian frame tightly. */
const lngArc = (lngs: number[]): { west: number; span: number } => {
	const sorted = [...lngs].sort((a, b) => a - b)
	let west = sorted[0]
	let widestGap = sorted[0] + 360 - sorted[sorted.length - 1]
	for (let i = 1; i < sorted.length; i++) {
		const gap = sorted[i] - sorted[i - 1]
		if (gap > widestGap) {
			widestGap = gap
			west = sorted[i]
		}
	}
	return { west, span: 360 - widestGap }
}

/**
 * The camera that fits every point inside the viewport's padded area, at a
 * whole zoom no closer than `maxZoom`; null when there is nothing to frame. A
 * single point frames at `maxZoom`.
 */
export const frameCamera = (points: LatLng[], viewport: ViewportSize, padding: FramePadding, maxZoom: number): Camera | null => {
	if (points.length === 0) return null

	const arc = lngArc(points.map(p => p.lng))
	const ys = points.map(p => toWorldY(p.lat))
	const north = Math.min(...ys)
	const south = Math.max(...ys)
	const xSpan = arc.span / 360
	const ySpan = south - north

	const usableWidth = Math.max(1, viewport.width - padding.left - padding.right)
	const usableHeight = Math.max(1, viewport.height - padding.top - padding.bottom)
	const fit = Math.min(Math.log2(usableWidth / (TILE_SIZE * xSpan)), Math.log2(usableHeight / (TILE_SIZE * ySpan)))
	const zoom = Math.max(0, Math.min(maxZoom, Math.floor(fit)))

	// Uneven padding moves the padded area's middle off the viewport's, and the centre with it.
	const scale = TILE_SIZE * 2 ** zoom
	const x = toWorldX(arc.west) + xSpan / 2 - (padding.left - padding.right) / (2 * scale)
	const y = (north + south) / 2 - (padding.top - padding.bottom) / (2 * scale)
	return { center: { lat: toLat(y), lng: toLng(x) }, zoom }
}
